/*
 * conditionalField — show/hide one form field cell based on another field's
 * current value.
 *
 * Applied by the form renderer to any field whose blueprint carries a
 * Statamic `if:` condition. The condition is read server-side (see
 * App\Support\FieldConditions) and handed over as data attributes, so this
 * component never parses Statamic's condition DSL:
 *
 *   data-condition-field   handle of the controlling field
 *   data-condition-values  candidate values, pipe-separated
 *   data-condition-match   "equals" (exact) | "contains" (substring)
 *
 * Progressive enhancement: without JS the field renders unconditionally
 * (`.no-js [x-cloak]` un-hides it) and stays completable. The authoritative
 * clear is server-side — App\Listeners\ClearHiddenConditionalFields blanks
 * any field whose condition did not pass, so a hidden field can never carry
 * a value into the submission regardless of what the browser did.
 *
 * The controlling field is found by name within the owning <form>. MH's
 * select primitive posts through a hidden input and dispatches a bubbling
 * native `change` on it (see select-field.js), so one delegated listener on
 * the form covers both the enhanced control and a plain native input. The
 * listener is bound in init() and removed in destroy() — Swup tears Alpine
 * trees down on navigation, and a listener left on a replaced form would
 * leak.
 */
export default function conditionalField() {
    return {
        visible: false,

        /*
         * Declared here, not first assigned in init(). Alpine resolves `this`
         * through a merge proxy over the whole x-data stack, and a write to a
         * key no scope in that stack owns yet lands on the OUTERMOST one — so
         * an undeclared `this.form = …` writes into the enclosing component
         * instead of this instance. Inside the form modal every conditional
         * cell across all three pre-rendered forms shares one `formModal`
         * scope, so the last cell to initialise overwrote `form` for all of
         * them and every other cell then evaluated against the wrong form
         * (MOHO-141). Declaring the keys gives each instance its own.
         */
        form: null,
        onFormChange: null,

        init() {
            this.onFormChange = () => this.evaluate();

            this.form = this.$el.closest('form');
            if (this.form) {
                // `input` as well as `change` so typing into a text control
                // that gates another field tracks live rather than on blur.
                this.form.addEventListener('change', this.onFormChange);
                this.form.addEventListener('input', this.onFormChange);
            }

            this.evaluate();
        },

        destroy() {
            if (this.form && this.onFormChange) {
                this.form.removeEventListener('change', this.onFormChange);
                this.form.removeEventListener('input', this.onFormChange);
            }
        },

        evaluate() {
            const wasVisible = this.visible;
            this.visible = this.passes();

            // Leaving a value behind in a field the user can no longer see
            // would submit an answer they never gave. Clearing on the
            // transition (not on every evaluation) keeps a server-rendered
            // value intact when the field is visible from the start, e.g. on
            // a validation-error re-render.
            if (wasVisible && !this.visible) {
                this.clear();
            }
        },

        passes() {
            const { conditionField, conditionValues, conditionMatch } = this.$el.dataset;

            if (!conditionField || !conditionValues) return true;

            const current = this.controllingValue(conditionField);
            if (current === '') return false;

            return conditionValues
                .split('|')
                .some((candidate) =>
                    conditionMatch === 'contains'
                        ? current.includes(candidate)
                        : current === candidate,
                );
        },

        /*
         * A field can be present twice: the enhanced control and the no-JS
         * fallback that shares its name. nojsFallback disables the fallback
         * at Alpine init, but ordering between components is not guaranteed,
         * so prefer whichever copy actually holds a value.
         */
        controllingValue(name) {
            if (!this.form) return '';

            const controls = Array.from(
                this.form.querySelectorAll(`[name="${CSS.escape(name)}"]`),
            ).filter((control) => !control.disabled);

            const answered = controls.find((control) => control.value !== '');

            return answered ? answered.value : '';
        },

        clear() {
            this.$el
                .querySelectorAll('input, textarea, select')
                .forEach((control) => {
                    control.value = '';
                });
        },
    };
}
